import type { ReactNode } from 'react';
import type { IcpRating } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { EmptyState } from '../../../components/system-states/SystemState';
import { LINE_RATING_WORDS, type LineFilterBlock, type LineFilterHelp, type LineFilterLine } from '../line-filters';
import styles from './ranking.module.css';

// ADR 0068: the list is empty because of the line filters. Say why in plain words and offer the lower ratings that would show people, so a
// first press that shows nobody doesn't feel broken. The rule behind the ratings is unchanged.

interface LineFilterEmptyProps {
  readonly help: LineFilterHelp;
  /** The same change the rating list makes: that line's filter goes to this rating. */
  readonly onSetRating: (lineId: string, rating: IcpRating) => void;
  readonly onTurnOff: (lineId: string) => void;
  readonly onClearAll: () => void;
}

const HIDDEN_NOTE = 'People Jev hasn’t read are hidden while a line filter is on.';

function peopleText(count: number): string {
  return `${count.toLocaleString('en-US')} ${count === 1 ? 'person' : 'people'}`;
}

function namesText(lines: readonly LineFilterLine[]): string {
  return lines.length === 1 ? `“${lines[0]?.name ?? ''}”` : 'these lines';
}

export function LineFilterEmpty(props: LineFilterEmptyProps): ReactNode {
  return (
    <div aria-live="polite">
      <Content {...props} />
    </div>
  );
}

function Content({ help, onSetRating, onTurnOff, onClearAll }: LineFilterEmptyProps): ReactNode {
  if (help.kind === 'relax') {
    const [only] = help.blocks;
    if (help.blocks.length === 1 && only) {
      return (
        <EmptyState
          title={`No one shows “${only.name}” at rating ${only.rating}`}
          body={`Rating ${only.rating} is “${LINE_RATING_WORDS[only.rating]}”. ${HIDDEN_NOTE} A lower rating would show people:`}
          action={
            <div className={styles.lineHelp}>
              <Suggestions block={only} onSetRating={onSetRating} />
              <Button variant="quiet" onClick={() => onTurnOff(only.lineId)}>
                Turn off this filter
              </Button>
            </div>
          }
          isInset
        />
      );
    }
    return (
      <EmptyState
        title="No one matches these line filters"
        body={`${HIDDEN_NOTE} Relaxing one line would show people:`}
        action={
          <div className={styles.lineHelp}>
            {help.blocks.map((block) => (
              <div key={block.lineId} className={styles.lineHelpBlock}>
                <span className={styles.lineHelpName}>{`“${block.name}” is at rating ${block.rating}`}</span>
                <Suggestions block={block} onSetRating={onSetRating} />
                <Button variant="quiet" onClick={() => onTurnOff(block.lineId)}>
                  Turn off this filter
                </Button>
              </div>
            ))}
            <Button variant="quiet" onClick={onClearAll}>
              Clear all
            </Button>
          </div>
        }
        isInset
      />
    );
  }

  const action = (
    <div className={styles.lineHelp}>
      {help.lines.map((line) => (
        <Button key={line.lineId} variant="quiet" onClick={() => onTurnOff(line.lineId)}>
          {help.lines.length === 1 ? 'Turn off this filter' : `Turn off “${line.name}”`}
        </Button>
      ))}
      {help.lines.length > 1 && (
        <Button variant="quiet" onClick={onClearAll}>
          Clear all
        </Button>
      )}
    </div>
  );
  if (help.kind === 'unread') return <EmptyState title={`No one has been read for ${namesText(help.lines)} yet`} body={HIDDEN_NOTE} action={action} isInset />;
  if (help.kind === 'nothing-found') return <EmptyState title={`Jev found nothing for ${namesText(help.lines)} in anyone it read`} body={`${HIDDEN_NOTE} Turn the filter off to see everyone.`} action={action} isInset />;
  return <EmptyState title="These line filters together match no one" body={`Relaxing one line on its own would still show nobody. ${HIDDEN_NOTE}`} action={action} isInset />;
}

function Suggestions({ block, onSetRating }: { readonly block: LineFilterBlock; readonly onSetRating: LineFilterEmptyProps['onSetRating'] }): ReactNode {
  return (
    <span className={styles.lineHelpButtons}>
      {block.suggestions.map((suggestion) => (
        <Button key={suggestion.rating} variant="outline" onClick={() => onSetRating(block.lineId, suggestion.rating)}>
          {`Rating ${suggestion.rating} · ${LINE_RATING_WORDS[suggestion.rating]} · ${peopleText(suggestion.count)}`}
        </Button>
      ))}
    </span>
  );
}
